
import React, { useState, useEffect, useMemo } from 'react';
import Button from './Button';
import { Story } from '../types';

interface LandingPageProps {
  onStart: () => void;
  onViewStory: (story: any) => void;
  onGoToLibrary: () => void;
  userExists: boolean;
  t: any;
  uiLang: string;
  langSelector: React.ReactNode;
  onLogoClick: () => void;
  communityStories?: any[];
}

const LandingPage: React.FC<LandingPageProps> = ({ 
  onStart, 
  onViewStory, 
  onGoToLibrary, 
  userExists, 
  t, 
  uiLang, 
  langSelector, 
  onLogoClick,
  communityStories = []
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [itemsToShow, setItemsToShow] = useState(4);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) setItemsToShow(1);
      else if (width < 768) setItemsToShow(2);
      else if (width < 1024) setItemsToShow(3);
      else setItemsToShow(4);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const displayStories = useMemo(() => {
    return communityStories || [];
  }, [communityStories]);

  useEffect(() => {
    if (isPaused || displayStories.length <= itemsToShow) {
      setCurrentIndex(0);
      return;
    }
    
    const timer = setInterval(() => {
      setCurrentIndex((prev) => {
        const next = prev + 1;
        if (next > displayStories.length - itemsToShow) return 0;
        return next;
      });
    }, 4500);
    
    return () => clearInterval(timer);
  }, [displayStories.length, isPaused, itemsToShow]);

  const features = [
    { icon: "✨", title: t.feat1Title, desc: t.feat1Desc, color: "bg-indigo-50", iconColor: "text-indigo-600" },
    { icon: "🎨", title: t.feat2Title, desc: t.feat2Desc, color: "bg-emerald-50", iconColor: "text-emerald-600" },
    { icon: "🗣️", title: t.feat3Title, desc: t.feat3Desc, color: "bg-amber-50", iconColor: "text-amber-600" },
    { icon: "📄", title: t.feat4Title, desc: t.feat4Desc, color: "bg-blue-50", iconColor: "text-blue-600" }
  ];

  return (
    <div className="flex flex-col min-h-screen w-full max-w-full overflow-x-hidden bg-[#fcfdfe]">
      {/* Navbar - Limitée à 7xl */}
      <nav className="fixed top-0 left-0 right-0 z-[100] px-4 sm:px-6 py-3">
        <div className="max-w-7xl mx-auto glass rounded-2xl px-4 sm:px-6 py-2 flex justify-between items-center shadow-lg border border-white/40">
          <button onClick={onLogoClick} className="flex items-center gap-2 group outline-none">
            <div className="shrink-0 w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-black text-lg shadow-md rotate-3 group-hover:rotate-0 transition-transform">ⵣ</div>
            <span className="text-base sm:text-lg font-bold text-indigo-950 tracking-tight hidden sm:block font-tifinagh truncate">ⵜⵉⵏⴼⵓⵙⵉⵏ ⵏ ⵉⵎⵥⵥⵢⴰⵏⵏ</span>
          </button>
          <div className="flex items-center gap-2 sm:gap-4">
            <button onClick={onGoToLibrary} className="text-indigo-600 font-bold text-xs px-3 py-1.5 hover:bg-indigo-50 rounded-lg transition-all">
              {t.library}
            </button>
            {langSelector}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 px-6 relative z-10 w-full">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="space-y-6 sm:space-y-8 text-center lg:text-left">
            <div className="inline-block">
              <span className="px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-full font-bold text-[10px] uppercase tracking-widest flex items-center gap-2">
                <span className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></span>
                {t.cultureTag}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-indigo-950 leading-tight tracking-tight">
              {t.heroTitle} <br/>
              <span className={`text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-emerald-500 ${uiLang === 'ber' ? 'font-tifinagh' : ''}`}>
                {t.heroSubtitle}
              </span>
            </h1>
            <p className="text-base sm:text-lg text-slate-500 leading-relaxed max-w-xl mx-auto lg:mx-0">
              {t.heroDesc}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
              <Button onClick={onStart} variant="primary" className="text-sm sm:text-base !py-3 !px-8 rounded-xl shadow-lg">
                {userExists ? t.newStory : t.startBtn}
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </Button>
            </div>
          </div>
          
          <div className="relative group w-full max-w-2xl mx-auto lg:max-w-none">
            <div className="relative rounded-[2rem] overflow-hidden border-4 border-white shadow-xl transition-all duration-700 bg-indigo-950">
              <img 
                src="https://img.freepik.com/free-photo/open-book-concept-fairy-tale-fiction-storytelling_23-2150793729.jpg?t=st=1767397929~exp=1767401529~hmac=523e99bf4ef6d2cab0d4a96b3ca6f7a29bddf8ec8d14d2bbd8d04aa27a93482b&w=1480" 
                alt="ⵜⵉⵏⴼⵓⵙⵉⵏ" 
                className="w-full h-auto object-cover aspect-[16/10] opacity-90 group-hover:opacity-100 transition-opacity duration-1000"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-20 px-6 bg-white relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 space-y-2">
            <h2 className="text-3xl sm:text-4xl font-black text-indigo-950">{t.featuresTitle}</h2>
            <div className="h-1.5 w-16 bg-indigo-600 mx-auto rounded-full"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feat, idx) => (
              <div key={idx} className={`${feat.color} p-8 rounded-[1.5rem] space-y-4 hover:scale-[1.03] transition-all border border-transparent hover:border-white hover:shadow-lg group`}>
                <div className="text-4xl group-hover:scale-110 transition-transform">{feat.icon}</div>
                <h3 className={`text-xl font-bold ${feat.iconColor}`}>{feat.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Showcase */}
      {displayStories.length > 0 && (
        <section className="py-16 sm:py-20 relative bg-indigo-50/20 w-full">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row justify-between items-center md:items-end gap-6 text-center md:text-left mb-8 sm:mb-12">
              <div className="space-y-2">
                 <h2 className="text-3xl sm:text-4xl font-black text-indigo-950 leading-tight">
                   {t.showcaseTitle}
                 </h2>
                 <p className="text-indigo-400 font-bold text-[10px] uppercase tracking-[0.2em]">{t.showcaseDesc}</p>
              </div>
              <Button variant="secondary" onClick={() => onGoToLibrary()} className="!py-2 !px-6 shadow-md !text-xs !rounded-lg border-indigo-100">
                {t.explore}
              </Button>
            </div>

            <div 
              className="relative w-full"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div className="overflow-hidden">
                <div 
                  className="flex transition-transform duration-[1200ms] ease-out -mx-3"
                  style={{ 
                    transform: `translateX(-${currentIndex * (100 / itemsToShow)}%)` 
                  }}
                >
                  {displayStories.map((story) => (
                    <div 
                      key={story.id} 
                      onClick={() => onViewStory(story)}
                      className="flex-none p-3"
                      style={{ width: `${100 / itemsToShow}%` }}
                    >
                      <div className="group bg-white rounded-2xl p-3 shadow-sm border border-indigo-100/50 cursor-pointer hover:shadow-xl transition-all duration-500 hover:-translate-y-2 h-full flex flex-col">
                        <div className="aspect-[3/4] rounded-xl overflow-hidden relative mb-4 bg-indigo-100 flex items-center justify-center border border-white">
                           {story.coverImage ? (
                             <img src={story.coverImage} alt={story.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                           ) : (
                             <span className="text-5xl font-tifinagh text-indigo-200 select-none">ⵣ</span>
                           )}
                          <div className="absolute top-3 left-3 z-20">
                            <span className="glass px-2 py-1 rounded-full text-[8px] font-black uppercase tracking-widest text-indigo-600 font-tifinagh">ⵜⵉⵏⴼⵓⵙⵉⵏ</span>
                          </div>
                        </div>
                        <div className="px-1 text-center mt-auto">
                          <h3 className="text-base font-bold text-indigo-950 font-tifinagh group-hover:text-indigo-600 transition-colors line-clamp-1">
                            {story.title}
                          </h3>
                          <div className="flex items-center justify-center gap-2 mt-1">
                             <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                             <p className="text-[8px] font-bold uppercase tracking-widest text-slate-400">Conte Magique</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer compact */}
      <footer className="py-12 text-center relative z-10 border-t border-indigo-50 bg-white">
        <div className="max-w-7xl mx-auto px-6">
           <div className="flex flex-col items-center gap-6">
              <button onClick={onLogoClick} className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-xl font-black text-indigo-600 hover:scale-110 transition-transform">ⵣ</button>
              <div className="space-y-2">
                <p className="text-indigo-950 font-bold uppercase tracking-[0.2em] text-xs font-tifinagh">ⵜⵉⵏⴼⵓⵙⵉⵏ ⵏ ⵉⵎⵥⵥⵢⴰⵏⵏ</p>
                <p className="text-slate-400 text-xs max-w-md mx-auto leading-relaxed">
                  Innovation technologique pour la préservation de la culture Amazighe.
                </p>
              </div>
              <p className="text-slate-300 text-[8px] font-bold uppercase tracking-[0.3em]">© 2025 ⵜⴰⵏⴼⵓⵙⵜ ⵎⴰⵔⵓⴽ</p>
           </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
